import { useEffect, useState } from 'react';
import type {
  PaymentAccountResourceModel,
  PaymentMethodFee,
  PaymentMethodFeeList,
} from 'api/models';
import type { ConsumerPaymentMethodSummaryModel } from './failed-payments.types';

type BillFeeApiClient = {
  post: <T>(url: string, body: unknown) => Promise<T>;
  get: <T>(url: string) => Promise<T>;
};

type BillTotalResponse = {
  amount: number;
};

type CreditCardPaymentMethodFee = Pick<
  PaymentMethodFee,
  'feeAmount' | 'totalWithFee'
>;

const CREDIT_CARD_FEE_RATE = 0.03;

function roundCurrency(amount: number) {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

function getCreditCardFeeFromAmount(
  amount: number,
): CreditCardPaymentMethodFee | undefined {
  if (!Number.isFinite(amount) || amount <= 0) return undefined;

  const feeAmount = roundCurrency(amount * CREDIT_CARD_FEE_RATE);
  return {
    feeAmount,
    totalWithFee: roundCurrency(amount + feeAmount),
  };
}

function usePaymentMethodFeesForBalanceIds(
  apiClient: BillFeeApiClient,
  balanceUniqueIds: string[],
) {
  const [fees, setFees] = useState<PaymentMethodFee[] | null>(null);
  const balanceUniqueIdsKey = balanceUniqueIds.join(',');

  useEffect(() => {
    if (!balanceUniqueIds.length) {
      return;
    }

    let cancelled = false;

    async function fetchFees() {
      try {
        const total = await apiClient.post<BillTotalResponse>(
          '/users/bills/total',
          { balances: balanceUniqueIds },
        );

        if (cancelled || !total.amount) return;

        const feeList = await apiClient.get<PaymentMethodFeeList>(
          `/users/bills/fees?amount=${total.amount}`,
        );

        if (!cancelled) {
          setFees(feeList.fees ?? null);
        }
      } catch {
        if (!cancelled) {
          setFees(null);
        }
      }
    }

    fetchFees();

    return () => {
      cancelled = true;
    };
  }, [apiClient, balanceUniqueIdsKey]);

  return balanceUniqueIds.length ? fees : null;
}

function usePaymentMethodFeesForAmount(
  apiClient: BillFeeApiClient,
  amount: number,
) {
  const [fees, setFees] = useState<PaymentMethodFee[] | null>(null);

  useEffect(() => {
    if (!amount) {
      return;
    }

    let cancelled = false;

    async function fetchFees() {
      try {
        const feeList = await apiClient.get<PaymentMethodFeeList>(
          `/users/bills/fees?amount=${amount}`,
        );

        if (!cancelled) {
          setFees(feeList.fees ?? null);
        }
      } catch {
        if (!cancelled) {
          setFees(null);
        }
      }
    }

    fetchFees();

    return () => {
      cancelled = true;
    };
  }, [apiClient, amount]);

  return amount ? fees : null;
}

function getCreditCardPaymentMethodFee({
  fees,
  paymentMethod,
  amount,
}: {
  fees: PaymentMethodFee[] | null;
  paymentMethod:
    | PaymentAccountResourceModel
    | ConsumerPaymentMethodSummaryModel
    | null;
  amount: number;
}) {
  if (paymentMethod?.paymentMethod !== 'CreditCard') return undefined;

  const matchingFee = fees?.find((fee) => {
    if (fee.paymentMethod !== paymentMethod.paymentMethod) return false;
    if (fee.feeAmount <= 0) return false;

    if ('isDomestic' in paymentMethod && 'isInternational' in paymentMethod) {
      return (
        (fee.isDomestic && paymentMethod.isDomestic) ||
        (fee.isInternational && paymentMethod.isInternational)
      );
    }

    return true;
  });

  return matchingFee ?? getCreditCardFeeFromAmount(amount);
}

export function useCreditCardFeeForBalanceIds({
  apiClient,
  balanceUniqueIds,
  paymentMethod,
  amount,
}: {
  apiClient: BillFeeApiClient;
  balanceUniqueIds: string[];
  paymentMethod:
    | PaymentAccountResourceModel
    | ConsumerPaymentMethodSummaryModel
    | null;
  amount: number;
}) {
  const fees = usePaymentMethodFeesForBalanceIds(apiClient, balanceUniqueIds);
  return getCreditCardPaymentMethodFee({ fees, paymentMethod, amount });
}

export function useCreditCardFeeForAmount({
  apiClient,
  paymentMethod,
  amount,
}: {
  apiClient: BillFeeApiClient;
  paymentMethod:
    | PaymentAccountResourceModel
    | ConsumerPaymentMethodSummaryModel
    | null;
  amount: number;
}) {
  const fees = usePaymentMethodFeesForAmount(apiClient, amount);
  return getCreditCardPaymentMethodFee({ fees, paymentMethod, amount });
}
